import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {alpha} from "@mui/material/styles";
import * as React from "react";
import {useTranslation} from "react-i18next";
import {CountryElement} from "../../../common/uiElements/CountryElement";
import {InsightsColumn, InsightsTable} from "../../kit/InsightsTable";
import {InsightsTooltip, TooltipRow, TooltipTitle} from "../../kit/InsightsTooltip";
import {SeriesCard} from "../../kit/SeriesCard";
import {CHART_HEIGHT} from "../../kit/insightsChart";
import type {InsightsLoading} from "../../kit/insightsLoading";
import {useBarGrowth} from "../../../surveys/insights/base/useBarGrowth";
import {
    BAR_DRAW_DURATION,
    BAR_HEIGHT,
    BAR_RADIUS,
    COMPARE_BAR_HEIGHT,
} from "../../../surveys/insights/constants";

const COMPARE_ALPHA = 0.35;

export interface DistributionRow {
    key: string;
    label: string;
    /** Renders the country's flag beside the label, for the rows that name a place. */
    countryCode?: string;
    value: number;
    compareValue?: number | null;
}

interface Props {
    title: string;
    hint: string;
    rows: DistributionRow[];
    loading?: InsightsLoading;
    comparing?: boolean;
    /** How one row's figure reads in full — "€12,480", "36 guests". */
    format: (value: number) => string;
    /** What the figure is, for the table's value column and the hover. */
    valueLabel: string;
    /**
     * How many rows the bars show. The table always lists every row, which is what the chart/table
     * toggle is for: a chart answers "who leads", a table answers "where is everyone else".
     */
    limit?: number;
    empty: string;
}

interface BarProps {
    share: number;
    height: number;
    grown: boolean;
    muted?: boolean;
}

function Bar({share, height, grown, muted}: BarProps) {
    return (
        <Box
            sx={theme => ({
                height,
                width: `${grown ? share : 0}%`,
                minWidth: share > 0 ? 2 : 0,
                borderRadius: `${BAR_RADIUS}px`,
                bgcolor: muted
                    ? alpha(theme.palette.primary.main, COMPARE_ALPHA)
                    : theme.palette.primary.main,
                transition: `width ${BAR_DRAW_DURATION}ms ease-out`,
            })}
        />
    );
}

/**
 * A ranked list read as bars: the name and its figure on one line, the bar under them, the comparison
 * as a thinner bar below that. The same shape the surveys module uses for its themes and breakdowns —
 * a bar chart library reading a category axis sideways is the thing this replaces.
 */
export function CrmDistributionCard({
    title,
    hint,
    rows,
    loading,
    comparing = false,
    format,
    valueLabel,
    limit,
    empty,
}: Props) {
    const {t} = useTranslation();

    const hasCompare = comparing && rows.some(row => row.compareValue != null);
    const hasData = rows.some(row => row.value > 0 || (row.compareValue ?? 0) > 0);
    // The API hands these back already ranked, so the leading rows are the first ones.
    const charted = limit == null ? rows : rows.slice(0, limit);
    const grown = useBarGrowth(`${charted.length}:${hasCompare}`);

    // Every bar is a share of the largest figure drawn, comparison included, so the two periods are
    // read against one scale rather than each against its own.
    const peak = Math.max(1, ...charted.flatMap(row => [row.value, row.compareValue ?? 0]));
    const share = (value: number) => Math.max(0, Math.min(100, (value / peak) * 100));

    const compareLabel = t("Comparison period");

    const columns: InsightsColumn<DistributionRow>[] = [
        {key: "label", label: title, kind: "label", cell: row => row.label},
        {key: "value", label: valueLabel, kind: "value", cell: row => format(row.value)},
        ...(hasCompare ? [{
            key: "compare",
            label: compareLabel,
            kind: "compare" as const,
            cell: (row: DistributionRow) => row.compareValue == null ? "—" : format(row.compareValue),
        }] : []),
    ];

    return (
        <SeriesCard
            title={title}
            hint={hint}
            loading={loading}
            empty={hasData ? undefined : (
                <Stack alignItems={"center"} justifyContent={"center"} sx={{height: CHART_HEIGHT, px: 3}}>
                    <Typography variant={"body2"} color={"text.secondary"} textAlign={"center"}>
                        {empty}
                    </Typography>
                </Stack>
            )}
            table={<InsightsTable label={title} columns={columns} rows={rows} rowKey={row => row.key}/>}
            chart={(
                <Stack gap={1.5} sx={{minWidth: 0, py: 0.5}}>
                    {charted.map(row => (
                        <InsightsTooltip
                            key={row.key}
                            placement={"top"}
                            title={(
                                <React.Fragment>
                                    <TooltipTitle title={row.label}/>
                                    <TooltipRow label={valueLabel} value={format(row.value)}/>
                                    {row.compareValue != null && (
                                        <TooltipRow label={compareLabel} value={format(row.compareValue)}/>
                                    )}
                                </React.Fragment>
                            )}
                        >
                            <Box
                                sx={theme => ({
                                    minWidth: 0,
                                    cursor: "default",
                                    px: 1,
                                    mx: -1,
                                    py: 0.75,
                                    my: -0.75,
                                    borderRadius: 1.5,
                                    transition: "background-color 120ms ease",
                                    "&:hover": {backgroundColor: alpha(theme.palette.text.primary, 0.05)},
                                })}
                            >
                                <Stack
                                    direction={"row"}
                                    alignItems={"baseline"}
                                    justifyContent={"space-between"}
                                    gap={2}
                                    sx={{mb: 0.75}}
                                >
                                    <Stack direction={"row"} alignItems={"center"} spacing={0.75} sx={{minWidth: 0}}>
                                        {row.countryCode && (
                                            <CountryElement countryCode={row.countryCode} shape={"flag"}/>
                                        )}

                                        <Typography
                                            sx={{color: "text.primary", fontSize: 14, fontWeight: 600}}
                                            noWrap
                                        >
                                            {row.label}
                                        </Typography>
                                    </Stack>

                                    <Typography
                                        sx={{
                                            color: "text.primary",
                                            fontSize: 14,
                                            fontWeight: 700,
                                            fontVariantNumeric: "tabular-nums",
                                            flexShrink: 0,
                                        }}
                                    >
                                        {format(row.value)}
                                    </Typography>
                                </Stack>

                                <Stack gap={0.5}>
                                    <Bar share={share(row.value)} height={BAR_HEIGHT} grown={grown}/>

                                    {hasCompare && (
                                        <Bar
                                            muted
                                            share={share(row.compareValue ?? 0)}
                                            height={COMPARE_BAR_HEIGHT}
                                            grown={grown}
                                        />
                                    )}
                                </Stack>
                            </Box>
                        </InsightsTooltip>
                    ))}
                    {charted.length < rows.length && (
                        <Typography variant={"body2"} color={"text.secondary"} sx={{pt: 0.5}}>
                            {t("{{count}} more in the table", {count: rows.length - charted.length})}
                        </Typography>
                    )}
                </Stack>
            )}
        />
    );
}

export default CrmDistributionCard;
